import { CSSProperties, FocusEvent, ReactNode, useCallback, useRef } from 'react'
import { twMerge } from 'tailwind-merge'

import { useClickOutside } from '@unreserved-frontend-v2/ui/hooks/use-click-outside/use-click-outside'

import { FlexCol } from '../flex/flex-col'

export interface BaseInputProps {
  id?: string
  name?: string
  label?: ReactNode
  isErrored?: boolean
  isRequired?: boolean
  placeholder?: string
  dataTestId?: string
  containerClassName?: string
  containerInlineStyle?: CSSProperties
  onValueChange(value: string | null): void
  onFocus?(event: FocusEvent): void
  onBlur?(event: FocusEvent): void
}

export type BaseStringInputProps = BaseInputProps &
  React.InputHTMLAttributes<HTMLInputElement> &
  React.PropsWithChildren

export const BaseStringInput = ({
  isErrored,
  label,
  containerClassName,
  containerInlineStyle,
  onValueChange,
  onFocus,
  onBlur,
  className,
  children,
  isRequired,
  ...rest
}: BaseStringInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  // For certain input fields, we need to know if the user clicked outside
  const handleClickOutsideInput = useCallback(() => {
    onBlur?.({} as FocusEvent)
  }, [onBlur])

  useClickOutside(inputRef, handleClickOutsideInput)

  return (
    <FlexCol className={containerClassName} style={containerInlineStyle}>
      {label}
      <input
        ref={inputRef}
        data-testid="base-string-input"
        onChange={(event) => onValueChange(event.target.value !== '' ? event.target.value : null)}
        onFocus={onFocus}
        onBlur={onBlur}
        className={twMerge(
          'h-10 w-full rounded border border-shades-200 pl-4 placeholder-shades outline-primary',
          // className must come before error state classes otherwise error state will not show
          className,
          isErrored ? 'border-red-600 bg-red-50 text-red-600 placeholder-red-400 outline-red-600' : ''
        )}
        required={isRequired}
        {...rest}
      />
      {children}
    </FlexCol>
  )
}
